import { useCallback, useEffect, useState } from 'react';
import { SearchHistoryEntry, SearchMode } from '../types/selectiveEditTypes';

const DEFAULT_MAX_ITEMS = 10;

/**
 * Read history from localStorage with backward-compatible migration.
 * Legacy format: string[]
 * New format: SearchHistoryEntry[]
 */
function readFromStorage(key: string): SearchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((item): SearchHistoryEntry | null => {
      // New structured entry
      if (item && typeof item === 'object' && typeof item.query === 'string') {
        return {
          query: item.query,
          mode: item.mode === 'regex' ? 'regex' : 'plain',
          caseSensitive: Boolean(item.caseSensitive),
        };
      }
      // Legacy string entry — migrate to structured
      if (typeof item === 'string') {
        return { query: item, mode: 'plain', caseSensitive: false };
      }
      return null;
    }).filter((v): v is SearchHistoryEntry => v !== null);
  } catch {
    return [];
  }
}

function writeToStorage(key: string, items: SearchHistoryEntry[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Check if two history entries are the same search (for deduplication). */
function entriesEqual(a: SearchHistoryEntry, b: SearchHistoryEntry): boolean {
  return a.query === b.query && a.mode === b.mode && a.caseSensitive === b.caseSensitive;
}

export function useFilterHistory(storageKey: string, maxItems = DEFAULT_MAX_ITEMS) {
  const [history, setHistory] = useState<SearchHistoryEntry[]>(() => readFromStorage(storageKey));

  // Re-read storage when storageKey changes
  useEffect(() => {
    setHistory(readFromStorage(storageKey));
  }, [storageKey]);

  const addEntry = useCallback((
    query: string,
    mode: SearchMode = 'plain',
    caseSensitive = false,
  ) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const entry: SearchHistoryEntry = { query: trimmed, mode, caseSensitive };
    setHistory((prev) => {
      const next = [entry, ...prev.filter((item) => !entriesEqual(item, entry))].slice(0, maxItems);
      writeToStorage(storageKey, next);
      return next;
    });
  }, [storageKey, maxItems]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // silently ignore
    }
  }, [storageKey]);

  return { history, addEntry, clearHistory };
}
